import { FC, ReactNode, createContext, useEffect, useReducer } from 'react';
import { UserResponseDTO } from 'src/models/user';
import {
  loginService,
  logoutService
} from 'src/services/user-management/login-services';
import { useFindUserByIdMutation } from 'src/services/user-management/user-services';
import axios from 'src/utils/axios';
import { UserTypeEnum } from 'src/utils/enum';

interface AuthState {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: UserResponseDTO | null;
}

interface AuthContextValue extends AuthState {
  method: 'JWT';
  login: (
    email: string,
    password: string,
    userType: UserTypeEnum
  ) => Promise<void>;
  logout: () => void;
  register: (email: string, name: string, password: string) => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

type InitializeAction = {
  type: 'INITIALIZE';
  payload: {
    isAuthenticated: boolean;
    user: UserResponseDTO | null;
  };
};

type LoginAction = {
  type: 'LOGIN';
  payload: {
    user: UserResponseDTO;
  };
};

type LogoutAction = {
  type: 'LOGOUT';
};

type RegisterAction = {
  type: 'REGISTER';
  payload: {
    user: UserResponseDTO;
  };
};

type Action = InitializeAction | LoginAction | LogoutAction | RegisterAction;

const initialAuthState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

const setSession = (
  jwtToken: string | null,
  user?: UserResponseDTO | null,
  userType?: string | null
): void => {
  if (jwtToken) {
    localStorage.setItem('jwtToken', jwtToken);

    if (user) {
      localStorage.setItem('id', JSON.stringify(user.id));
    }

    if (userType) {
      localStorage.setItem('userType', JSON.stringify(userType));
    }

    axios.defaults.headers.common.Authorization = `Bearer ${jwtToken}`;
  } else {
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('id');
    localStorage.removeItem('userType');
    delete axios.defaults.headers.common.Authorization;
  }
};

const handlers: Record<
  string,
  (state: AuthState, action: Action) => AuthState
> = {
  INITIALIZE: (state: AuthState, action: InitializeAction): AuthState => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user
    };
  },
  LOGIN: (state: AuthState, action: LoginAction): AuthState => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  },
  LOGOUT: (state: AuthState): AuthState => ({
    ...state,
    isAuthenticated: false,
    user: null
  }),
  REGISTER: (state: AuthState, action: RegisterAction): AuthState => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  }
};

const reducer = (state: AuthState, action: Action): AuthState =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext<AuthContextValue>({
  ...initialAuthState,
  method: 'JWT',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve()
});

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const [findUserById, findUserByIdRes] = useFindUserByIdMutation();

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        const jwtToken = window.localStorage.getItem('jwtToken');
        const id = window.localStorage.getItem('id');

        if (jwtToken) {
          setSession(jwtToken);

          await findUserById(parseInt(id))
            .unwrap()
            .then((userResponse) => {
              window.sessionStorage.setItem(
                'permissions',
                JSON.stringify(userResponse.permissionDTOList)
              );
              dispatch({
                type: 'INITIALIZE',
                payload: {
                  isAuthenticated: true,
                  user: userResponse
                }
              });
            });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null
            }
          });
        }
      } catch (err) {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };

    initialize();
  }, []);

  const login = async (
    email: string,
    password: string,
    userType: UserTypeEnum
  ): Promise<void> => {
    const loginResponse = await loginService({
      username: email,
      password,
      userType
    });
    const { jwtToken } = loginResponse.data;
    axios.defaults.headers.common.Authorization = `Bearer ${jwtToken}`;

    await findUserById(loginResponse.data.id)
      .unwrap()
      .then((userResponse) => {
        window.location.reload();
        setSession(jwtToken, userResponse, userType);
        dispatch({
          type: 'LOGIN',
          payload: {
            user: userResponse
          }
        });
        window.sessionStorage.setItem(
          'permissions',
          JSON.stringify(userResponse.permissionDTOList)
        );
        localStorage.setItem(
          'permissions',
          JSON.stringify(userResponse.permissionDTOList)
        );
      });
    // updateAuthenticationStatus();
  };

  const logout = async (): Promise<void> => {
    logoutService();
    setSession(null);
    sessionStorage.clear();
    localStorage.clear();
    dispatch({ type: 'LOGOUT' });
    // updateAuthenticationStatus();
  };

  const register = async (
    email: string,
    name: string,
    password: string
  ): Promise<void> => {
    // const response = await axios.post<{ jwtToken: string; user: User }>(
    //   '/api/account/register',
    //   {
    //     email,
    //     name,
    //     password
    //   }
    // );
    // const { jwtToken } = response.data;
    // const { user } = response.data;
    // window.localStorage.setItem('jwtToken', jwtToken);
    // dispatch({
    //   type: 'REGISTER',
    //   payload: {
    //     user: user
    //   }
    // });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'JWT',
        login,
        logout,
        register
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
